import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { InterviewDetailsAPI } from '../../../api/InterviewDetailsAPI';
import { SelectWithDescriptionOption } from '@/components/formBuilder/SelectComponents';
import { http } from '@/lib/http';
import { useScorecardVisibilitySettings } from '@/api/scorecard-visibility';
import { SchNoteTakerField } from './SchNoteTakerField';
import { SchFillWithAiField } from './SchFillWithAiField';
import { SchZoomHost } from '../SchZoomHost';
import * as Yup from 'yup';
import { SchLocation } from './SchLocation';

const getHackerRankInterviewTemplates = async () => {
  const response = await http.get('/hacker_rank/list_interview_templates');
  return response;
};

export const useSchLocationField = ({
  registerForUpdates,
  updateSharedData,
  interviewPlan,
  locationOptions,
  isEdit,
  formRef,
  interviewDataRef,
  isSelfSchedule,
  interviewPlatform,
  showScorecardStep = false,
}) => {
  const { data: scorecardVisibilityData } = useScorecardVisibilitySettings();

  // Fetch office locations for onsite selection
  const { data: offices = [], isLoading: isOfficesLoading } = useQuery({
    queryKey: ['offices'],
    queryFn: () => InterviewDetailsAPI.getOffices(),
  });

  const { data: hackerrankTemplates, isLoading: isLoadingTemplates } = useQuery(
    {
      queryKey: ['hackerrank', 'interview-templates'],
      queryFn: getHackerRankInterviewTemplates,
      staleTime: 0,
    }
  );

  const templateOptions = useMemo(() => {
    return Array.isArray(hackerrankTemplates?.data)
      ? hackerrankTemplates?.data?.length > 0
        ? hackerrankTemplates?.data?.map((template) => ({
            label: template?.name,
            value: String(template?.id),
          }))
        : [{ label: 'No Template Available', value: '', isReadOnly: true }]
      : [];
  }, [hackerrankTemplates?.data]);

  const handleTemplateChange = useCallback(
    (selectedValue) => {
      if (selectedValue && formRef?.current) {
        formRef.current.setFieldValue(
          'ext_interview_template_id',
          selectedValue.value
        );
      }
    },
    [formRef]
  );

  const locationFieldConfig = useMemo(
    () => ({
      isConditional: true,
      conditionConfig: {
        field: 'interviewers',
        type: 'custom',
        customFunc: (_, allValues) => {
          const { interviewers } = allValues ?? {};
          const isAtlLeastOneInterviewer = Array.isArray(interviewers)
            ? interviewers?.length > 0
            : interviewers;
          return isAtlLeastOneInterviewer;
        },
      },
      name: 'location',
      label: 'Location',
      type: 'custom',
      isRequired: true,
      validation: Yup.string().required('Location is required'),
      Field: () => (
        <SchLocation
          registerForUpdates={registerForUpdates}
          updateSharedData={updateSharedData}
          interviewPlan={interviewPlan}
          formRef={formRef}
          locationOptions={locationOptions}
          interviewDataRef={interviewDataRef}
          isSelfSchedule={isSelfSchedule}
          interviewPlatform={interviewPlatform}
        />
      ),
    }),
    [
      formRef,
      interviewDataRef,
      interviewPlan,
      locationOptions,
      isSelfSchedule,
      registerForUpdates,
      updateSharedData,
      interviewPlatform,
    ]
  );

  // Zoom host field configuration
  const zoomHostConfig = useMemo(
    () => ({
      isConditional: true,
      conditionConfig: {
        field: 'location',
        type: 'custom',
        customFunc: (value, allValues) => {
          const { interviewers } = allValues ?? {};
          const isAtlLeastOneInterviewer = Array.isArray(interviewers)
            ? interviewers?.length > 0
            : interviewers;
          return value === 'zoom' && isAtlLeastOneInterviewer;
        },
      },
      name: 'host_id',
      label: 'Host',
      type: 'custom',
      isRequired: true,
      validation: Yup.mixed().test(
        'host-required-only-for-zoom',
        'Host is required',
        function (value) {
          const { location } = this.parent || {};
          if (location !== 'zoom') {
            // Don't validate host_id for non-zoom locations
            return true;
          }
          // For zoom, require host_id to be a non-null, non-undefined, non-empty value
          if (value === null || value === undefined || value === '') {
            return this.createError({ message: 'Host is required' });
          }
          if (Array.isArray(value)) {
            if (
              value.length === 0 ||
              value.some((v) => v === null || v === undefined || v === '')
            ) {
              return this.createError({ message: 'Host is required' });
            }
          }
          return true;
        }
      ),
      Field: () => (
        <SchZoomHost
          isEdit={isEdit}
          interviewPlan={interviewPlan}
          isSelfSchedule={isSelfSchedule}
          interviewDataRef={interviewDataRef}
          registerForUpdates={registerForUpdates}
          updateSharedData={updateSharedData}
        />
      ),
    }),
    [
      interviewDataRef,
      interviewPlan,
      isEdit,
      isSelfSchedule,
      registerForUpdates,
      updateSharedData,
    ]
  );

  // Office address field configuration
  const officeAddressConfig = useMemo(
    () => ({
      isConditional: true,
      conditionConfig: {
        field: 'location',
        type: 'custom',
        customFunc: (_, allValues) => {
          const { interviewers, location } = allValues ?? {};
          const isAtlLeastOneInterviewer = Array.isArray(interviewers)
            ? interviewers?.length > 0
            : interviewers;
          return isAtlLeastOneInterviewer && location === 'onsite';
        },
      },
      name: 'ats_office_id',
      id: 'ats_office_id',
      label: 'Office Address',
      type: 'select',
      isRequired: true,
      validation: Yup.string().when('location', {
        is: 'onsite',
        then: (schema) =>
          schema.required('Office address is required for onsite interviews'),
        otherwise: (schema) => schema.nullable(),
      }),
      options: offices,
      containerStyles: { width: '100%' },
      placeholder: 'Select office address',
      loading: isOfficesLoading,
      valueKey: 'id',
      labelKey: 'name',
      isMulti: false,
      descKey: 'location',
      components: {
        Option: SelectWithDescriptionOption,
      },
      customOnChange: (option) => {
        const { setFieldValue } = formRef?.current ?? {};
        setFieldValue('ats_office', option);
      },
    }),
    [offices, isOfficesLoading, formRef]
  );

  // HackerRank template field configuration
  const hackerrankTemplateConfig = useMemo(
    () => ({
      isConditional: true,
      conditionConfig: {
        field: 'location',
        type: 'custom',
        customFunc: (_, allValues) => {
          const { interviewers, location } = allValues ?? {};
          const isAtlLeastOneInterviewer = Array.isArray(interviewers)
            ? interviewers?.length > 0
            : interviewers;
          return isAtlLeastOneInterviewer && location === 'hackerrank';
        },
      },
      name: 'ext_interview_template_id',
      id: 'ext_interview_template_id',
      label: 'Template',
      type: 'kSelect',
      menuPlacement: 'top',
      isRequired: false,
      placeholder: 'Select template',
      width: '100%',
      containerStyles: { width: '100%' },
      options: templateOptions,
      isLoading: isLoadingTemplates,
      customOnChange: handleTemplateChange,
    }),
    [templateOptions, isLoadingTemplates, handleTemplateChange]
  );

  // Note taker field configuration
  const noteTakerConfig = useMemo(
    () => ({
      isConditional: true,
      conditionConfig: {
        field: 'location',
        type: 'custom',
        customFunc: (_, allValues) => {
          const { interviewers, location } = allValues ?? {};
          const isAtlLeastOneInterviewer = Array.isArray(interviewers)
            ? interviewers?.length > 0
            : interviewers;
          const isValidLocation = ['zoom', 'google_meet', 'teams'].includes(
            location
          );
          return isAtlLeastOneInterviewer && isValidLocation;
        },
      },
      name: 'note_taker_enabled',
      id: 'note_taker_enabled',
      type: 'custom',
      containerStyles: { width: '100%' },
      Field: (props) => (
        <SchNoteTakerField {...props} updateSharedData={updateSharedData} />
      ),
    }),
    [updateSharedData]
  );

  // Fill with AI field: show in form only when Scorecard step is not in the flow
  const fillWithAiAccountEnabled =
    scorecardVisibilityData?.data?.account_setting?.fill_with_ai_enabled;
  const fillWithAiConfig = useMemo(
    () => ({
      isConditional: true,
      conditionConfig: {
        field: 'location',
        type: 'custom',
        customFunc: (_, allValues) => {
          const { interviewers, location, note_taker_enabled } =
            allValues ?? {};
          const isAtlLeastOneInterviewer = Array.isArray(interviewers)
            ? interviewers?.length > 0
            : interviewers;
          const isValidLocation = ['zoom', 'google_meet', 'teams'].includes(
            location
          );
          return (
            isAtlLeastOneInterviewer &&
            isValidLocation &&
            note_taker_enabled === true
          );
        },
      },
      name: 'fill_with_ai_enabled',
      id: 'fill_with_ai_enabled',
      type: 'custom',
      containerStyles: { width: '100%' },
      Field: (props) => (
        <SchFillWithAiField {...props} updateSharedData={updateSharedData} />
      ),
    }),
    [updateSharedData]
  );

  // Combine all field configurations
  const locationConfigs = useMemo(
    () =>
      [
        locationFieldConfig,
        zoomHostConfig,
        officeAddressConfig,
        hackerrankTemplateConfig,
        noteTakerConfig,
        ...(fillWithAiAccountEnabled && !showScorecardStep
          ? [fillWithAiConfig]
          : []),
      ].filter(Boolean),
    [
      locationFieldConfig,
      zoomHostConfig,
      officeAddressConfig,
      hackerrankTemplateConfig,
      noteTakerConfig,
      fillWithAiConfig,
      fillWithAiAccountEnabled,
      showScorecardStep,
    ]
  );

  return locationConfigs;
};
